import React, { useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import { Box, Flex } from 'reflexbox/styled-components'
import PropTypes from 'prop-types'
import debounce from 'lodash/debounce'
import useAnalytics from 'lib/analytics/useAnalytics'
import {
  EVENT_TEST_DRIVE_BANNER_OPENED,
  EVENT_TEST_DRIVE_BANNER_CLICKED,
} from 'lib/analytics/events'

import IconChevronDown from '@anvilco-pkg/ui/components/Icon/IconChevronDown'

import { H5 } from 'lib/ui/primitives'
import getStarry from 'lib/ui/starry'

import UniversalLink from 'components/UniversalLink'
import LogoTestDrive from 'components/logo/LogoTestDrive'
import Uploader from 'components/EmbedForm/test-drive/Uploader'
import { SCROLL_NAME as ETCH_SCROLL_NAME } from 'components/test-drive/EtchTestDrive'
import { SCROLL_NAME as PDF_SCROLL_NAME } from 'components/test-drive/PDFTemplatesTestDrive'

import theme from 'theme'
import media from 'media'
import { isHTMLDocument } from 'helpers'

const TRANSITION_S = '0.8s'
export const BANNER_HEIGHT = 58
const starry = getStarry({ mode: 'less' })

const configWithExpanded = {
  shouldForwardProp: (prop) => ['isExpanded'].indexOf(prop) === -1,
}

const StyledCTA = styled.span`
  color: ${theme.colors.green};
  text-decoration: underline;

  &:hover {
    color: ${theme.colors.white};
  }
`

const BannerContainer = styled(Box)`
  display: none;
  position: absolute;
  right: 0;
  left: 0;
  top: 0;

  ${media.desktop`
    display: block;
  `}
`

const BackgroundWrapper = styled(Box).withConfig(configWithExpanded).attrs({
  bg: theme.colors.black,
})`
  position: relative;
  height: auto;
  overflow: hidden;
  font-size: 16px;
  z-index: 100;
  transition: height ${TRANSITION_S};
`

const StyledUniversalLink = styled(UniversalLink)`
  color: ${theme.colors.green};
  text-decoration: none;
  display: block;
`

const ChevronContainer = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  padding: 18px;
  cursor: pointer;
  z-index: 5;
`

const StyledIconChevronDown = styled(IconChevronDown).withConfig(
  configWithExpanded
)`
  cursor: pointer;
  transform: rotate(${({ isExpanded }) => (isExpanded ? '180deg' : '0deg')});
  transition: transform ${TRANSITION_S};
`

const BannerText = styled(Box)
  .attrs({
    color: theme.colors.white,
    lineHeight: `${BANNER_HEIGHT}px`,
    textAlign: 'center',
    width: '100%',
  })
  .withConfig({
    shouldForwardProp: (prop) => ['isExpanded'].indexOf(prop) === -1,
  })`
  position: relative;
  z-index: 2;

  transition: opacity ${TRANSITION_S}, visibility ${TRANSITION_S}, color ${TRANSITION_S}, max-height ${TRANSITION_S};
  ${({ isExpanded }) =>
    !isExpanded
      ? `
    opacity: 1;
    visibility: visible;
    display: block;
    max-height: ${BANNER_HEIGHT}px;
  `
      : `
    opacity: 0;
    visibility: hidden;
    color: transparent;
    max-height: 0;
  `}
`

const ExpandedContainer = styled(Flex)
  .attrs({
    maxWidth: theme.paperMaxWidth,
    color: theme.colors.white,
    margin: '0 auto',
    px: [20, 20, 20, 50],
    alignItems: 'center',
    justifyContent: 'space-between',
  })
  .withConfig({
    shouldForwardProp: (prop) => ['isExpanded'].indexOf(prop) === -1,
  })`
  gap: 20px;
  z-index: 2;

  transition: opacity ${TRANSITION_S}, visibility ${TRANSITION_S}, max-height ${TRANSITION_S}, color ${TRANSITION_S};
  ${({ isExpanded }) =>
    isExpanded
      ? `
    opacity: 1;
    visibility: visible;
    height: auto;
    max-height: 1000px;
  `
      : `
    opacity: 0;
    visibility: hidden;
    max-height: 0;
    color: transparent;
  `}
`

const TestDriveTitle = styled(H5).attrs({
  as: 'div',
  mt: 10,
  fontWeight: theme.fontWeights.normal,
  fontSize: 36,
})`
  span {
    color: ${theme.colors.green};
  }
`

const StarWrapper = styled(Box)`
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  z-index: -5;
`

const TestDriveDescription = styled(Box).attrs({ mt: 8, maxWidth: 390 })``

const AITopBanner = ({ text, ctaText, to, testDrive }) => {
  const [isExpanded, setExpanded] = useState(false)
  const bannerRef = useRef(null)
  const bannerOpenEventHandler = useAnalytics({
    eventName: EVENT_TEST_DRIVE_BANNER_OPENED,
  })
  const bannerClickEventHandler = useAnalytics({
    eventName: EVENT_TEST_DRIVE_BANNER_CLICKED,
  })

  useEffect(() => {
    if (!isHTMLDocument()) return
    const handleClickOutside = (event) => {
      if (bannerRef.current && !bannerRef.current.contains(event.target)) {
        setExpanded(false)
      }
    }

    const handleScroll = () => {
      setExpanded(false)
    }

    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('scroll', handleScroll)

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('scroll', handleScroll)
    }
  }, [])

  const handleMouseEnterDebounced = debounce(() => {
    bannerOpenEventHandler()
    setExpanded(true)
  }, 1500)

  const handleMouseLeave = () => {
    handleMouseEnterDebounced.cancel()
  }

  const handleBannerClick = () => {
    if (isExpanded) {
      bannerClickEventHandler()
    }
  }

  const ctaTo = !testDrive
    ? to
    : testDrive === 'etch'
      ? ETCH_SCROLL_NAME
      : PDF_SCROLL_NAME

  return (
    <BannerContainer
      onMouseEnter={handleMouseEnterDebounced}
      onMouseLeave={handleMouseLeave}
      onClick={handleBannerClick}
      ref={bannerRef}
    >
      <BackgroundWrapper className={starry.className}>
        <StarWrapper>{starry.children}</StarWrapper>
        {testDrive && (
          <ChevronContainer onClick={() => setExpanded((prev) => !prev)}>
            <StyledIconChevronDown
              fill={theme.colors.white}
              isExpanded={isExpanded}
            />
          </ChevronContainer>
        )}
        <BannerText
          isExpanded={isExpanded}
          as={testDrive ? 'div' : StyledUniversalLink}
          to={ctaTo}
        >
          {text} <StyledCTA>{ctaText}</StyledCTA>
        </BannerText>
        <ExpandedContainer isExpanded={isExpanded}>
          <Box>
            <LogoTestDrive />
            <TestDriveTitle>
              {testDrive === 'pdf-templates' ? (
                <>
                  Turn your PDF into an
                  <br />
                  API-fillable template in <span>seconds</span>
                </>
              ) : (
                <>
                  Automatically generate
                  <br />
                  e-signature packets by <span>uploading</span> your PDF.
                </>
              )}
            </TestDriveTitle>
            <TestDriveDescription>
              Upload any PDF to see Anvil's Document AI in action. No signup
              required.
            </TestDriveDescription>
          </Box>
          <Uploader mode={testDrive} inTopBanner style={{ zIndex: 10 }} />
        </ExpandedContainer>
      </BackgroundWrapper>
    </BannerContainer>
  )
}

AITopBanner.propTypes = {
  text: PropTypes.string,
  ctaText: PropTypes.string,
  to: PropTypes.string,
  testDrive: PropTypes.oneOf(['etch', 'pdf-templates']),
}

export default AITopBanner
